import type { Collector } from '@/types/collector';
import { getApiClient } from '@/lib/api/api-client';
import { CacheService } from '../cache';
import { TiersAPI } from '@/lib/services/insights/tiers.service';

export interface PaginatedResponse<T> {
  docs: T[];
  totalDocs: number;
  limit: number;
  sort: string;
  totalPages: number;
  page: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
}

export interface BaseQueryParams {
  page?: number;
  limit?: number;
  where?: Record<string, any>;
  select?: Record<string, boolean>;
  depth?: number;
  sort?: string;
}

export interface LeaderboardQueryParams extends BaseQueryParams {
  page?: number;
  limit?: number;
  query?: string;
  tier?: number;
  sort?: string;
  depth?: number;
  activeTab?: string;
  where?: Record<string, any>;
}

/**
 * Collectors API service
 */
export class CollectorsAPI {
  static apiClient = getApiClient();
  private static CACHE_DURATION = 5 * 60 * 1000;

  /**
   * Get all collectors data
   */
  static async getAllCollectors(params: Partial<LeaderboardQueryParams> = {}): Promise<PaginatedResponse<Collector>> {
    const { query, page = 1, tier, sort, ...rest } = params;

    const sortQuery = sort || 'rank'; // varsayılan sıralama

    const where = {
      calculateGuild: {
        equals: true,
      },
      'tradingStats.totalOwned': {
        greater_than: 0,
      },
      ...(tier && {
        tiers: {
          equals: tier,
        },
      }),
      ...(query && {
        username: { contains: query },
      }),
      ...rest.where,
    };

    const cacheKey = 'allCollectorsData' + JSON.stringify(params);
    const cachedData = CacheService.getFromCache(cacheKey);
    if (cachedData) {
      return cachedData as PaginatedResponse<Collector>;
    }

    const collectorsData = await CollectorsAPI.apiClient.collections.collectors.find({
      select: {
        username: true,
        mainWalletAddress: true,
        tiers: true,
        badges: true,
        rank: true,
        tradingStats: {
          totalOwned: true,
          firstPurchaseAt: true,
          tradedVolume: true,
          totalCollection: true,
          portfolioValue: true,
        },
      },
      limit: 10,
      depth: 2,
      sort: sortQuery as any,
      page,
      where,
    });

    CacheService.addToCache(cacheKey, collectorsData, CollectorsAPI.CACHE_DURATION);
    return collectorsData as PaginatedResponse<Collector>;
  }

  /**
   * Get top collectors data
   */
  static async getTopCollectors(): Promise<Collector[] | null> {
    const cacheKey = 'topCollectorsData';
    const cachedData = CacheService.getFromCache(cacheKey);
    if (cachedData) {
      return cachedData as Collector[];
    }

    const collectorsData = await CollectorsAPI.apiClient.collections.collectors.find({
      select: {
        username: true,
        mainWalletAddress: true,
        tiers: true,
        badges: true,
        tradingStats: {
          totalOwned: true,
          firstPurchaseAt: true,
          tradedVolume: true,
          totalCollection: true,
          portfolioValue: true,
        },
      },
      where: {
        calculateGuild: {
          equals: true,
        },
        tiers: {
          exists: true,
        },
      },
      limit: 3,
      depth: 2,
      sort: 'rank',
    });

    CacheService.addToCache(cacheKey, collectorsData.docs, CollectorsAPI.CACHE_DURATION);
    return collectorsData.docs;
  }

  /**
   * Get notable collectors data
   */
  static async getNotableCollectors(): Promise<Collector[] | null> {
    const cacheKey = 'notableCollectorsData';
    const cachedData = CacheService.getFromCache(cacheKey);
    if (cachedData) {
      return cachedData as Collector[];
    }

    const collectorsData = await CollectorsAPI.apiClient.collections.collectors.find({
      select: {
        username: true,
        mainWalletAddress: true,
        tiers: true,
        badges: true,
        tradingStats: {
          totalOwned: true,
          firstPurchaseAt: true,
          tradedVolume: true,
          totalCollection: true,
          portfolioValue: true,
        },
      },
      where: {
        calculateGuild: {
          equals: true,
        },
        tiers: {
          exists: true,
        },
        notable: {
          equals: true,
        },
      },
      limit: 3,
      depth: 2,
      sort: 'rank',
    });

    CacheService.addToCache(cacheKey, collectorsData.docs, CollectorsAPI.CACHE_DURATION);
    return collectorsData.docs;
  }

  /**
   * Get most active collectors data
   */
  static async getMostActiveCollectors(): Promise<Collector[] | null> {
    const cacheKey = 'mostActiveCollectorsData';
    const cachedData = CacheService.getFromCache(cacheKey);
    if (cachedData) {
      return cachedData as Collector[];
    }

    const collectorsData = await CollectorsAPI.apiClient.collections.collectors.find({
      select: {
        username: true,
        mainWalletAddress: true,
        tiers: true,
        badges: true,
        tradingStats: {
          totalOwned: true,
          firstPurchaseAt: true,
          tradedVolume: true,
          totalCollection: true,
          portfolioValue: true,
        },
      },
      where: {
        calculateGuild: {
          equals: true,
        },
        tiers: {
          exists: true,
        },
      },
      limit: 3,
      depth: 2,
      sort: '-tradingStats.totalOwned' as any,
    });

    CacheService.addToCache(cacheKey, collectorsData.docs, CollectorsAPI.CACHE_DURATION);
    return collectorsData.docs;
  }

  // Get Collector data
  static async getCollectorData() {
    const topCollectors = await CollectorsAPI.getTopCollectors();
    const notableCollectors = await CollectorsAPI.getNotableCollectors();
    const mostActiveCollectors = await CollectorsAPI.getMostActiveCollectors();
    const tiersData = await TiersAPI.getTiersData();
    return {
      topCollectors,
      notableCollectors,
      mostActiveCollectors,
      tiersData,
    };
  }
}
